import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Briefcase, 
  Building2, 
  ChevronLeft, 
  ChevronRight, 
  DollarSign, 
  Clock, 
  MapPin, 
  ArrowUpRight, 
  Sparkles, 
  CheckCircle2, 
  ArrowRight
} from 'lucide-react';
import { supabase } from '../../lib/supabase';

export interface VacancyItem {
  id: string;
  title: string;          // Functie
  company: string;        // Bedrijf
  salary: string;         // Salaris
  hours: string;          // Uren
  location: string;       // Locatie / Remote
  logoUrl?: string;
  verified?: boolean;
  featured?: boolean;
  tags?: string[];
}

const FALLBACK_VACANCIES: VacancyItem[] = [
  {
    id: 'job-wersee-fullstack',
    title: 'Senior Full-Stack Developer',
    company: 'Wersee Studio',
    salary: '€4.500 - €6.200 / mnd',
    hours: '36 - 40 uur',
    location: 'Remote (NL/EU)',
    verified: true,
    featured: true,
    tags: ['React', 'TypeScript', 'Node.js'],
  },
  {
    id: 'job-horizon-ai-product',
    title: 'AI Product & Prompt Specialist',
    company: 'Horizon Labs AI',
    salary: '€4.200 - €5.600 / mnd',
    hours: '32 - 40 uur',
    location: 'Hybride · Amsterdam',
    verified: true,
    featured: true,
    tags: ['AI / LLM', 'Product', 'Innovation'],
  },
  {
    id: 'job-pulse-designer',
    title: 'Lead UI/UX & Brand Designer',
    company: 'Studio Pulse',
    salary: '€3.800 - €5.000 / mnd',
    hours: '32 - 36 uur',
    location: 'Amsterdam · Op kantoor',
    verified: true,
    featured: false,
    tags: ['Figma', 'Design Systems', 'Creative'],
  },
  {
    id: 'job-cloudscale-devops',
    title: 'Cloud Infrastructure Engineer',
    company: 'CloudScale Solutions',
    salary: '€4.800 - €6.500 / mnd',
    hours: '40 uur',
    location: 'Remote (NL)',
    verified: true,
    featured: false,
    tags: ['DevOps', 'AWS', 'Kubernetes'],
  },
  {
    id: 'job-community-growth',
    title: 'Community & Ecosystem Lead',
    company: 'Wersee Communities',
    salary: '€3.500 - €4.700 / mnd',
    hours: '32 - 40 uur',
    location: 'Remote · Flexibel',
    verified: true,
    featured: true,
    tags: ['Growth', 'Creators', 'Community'],
  },
  {
    id: 'job-vanguard-marketing',
    title: 'Performance Marketeer & Growth',
    company: 'Vanguard Media Group',
    salary: '€3.400 - €4.600 / mnd',
    hours: '36 uur',
    location: 'Rotterdam · Hybride',
    verified: true,
    featured: false,
    tags: ['Analytics', 'Ads', 'Growth'],
  },
];

export const HomeVacanciesSection: React.FC = () => {
  const [vacancies, setVacancies] = useState<VacancyItem[]>(FALLBACK_VACANCIES);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const sliderRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let active = true;
    const fetchLiveJobs = async () => {
      try {
        const { data, error } = await supabase
          .from('listings')
          .select('id, title, price, metadata, profiles!listings_seller_id_fkey(username, full_name, avatar_url)')
          .eq('type', 'job')
          .order('created_at', { ascending: false })
          .limit(10);

        if (error) {
          console.warn('Could not load live jobs for homepage slider, using showcase jobs:', error.message);
          return;
        }

        if (active && data && data.length > 0) {
          const mapped: VacancyItem[] = data.map((item: any) => {
            const meta = item.metadata || {};
            const salary = meta.salaryRange 
              ? `${meta.currency || '€'}${meta.salaryRange}`
              : item.price > 0 ? `€${item.price} / uur` : 'Salaris in overleg';
            
            const hours = meta.hours || meta.jobType || (meta.jobType === 'part-time' ? '24 - 32 uur' : '36 - 40 uur');
            const company = meta.company || item.profiles?.full_name || item.profiles?.username || 'Wersee Bedrijf';
            const location = meta.locationType === 'remote' ? 'Remote' : (meta.locationAddress || meta.location || 'Nederland');

            return {
              id: item.id,
              title: item.title,
              company,
              salary,
              hours,
              location,
              logoUrl: meta.logoUrl || item.profiles?.avatar_url,
              verified: true,
              featured: meta.featured ?? false,
              tags: meta.skills?.slice(0, 3) || ['Vacature'],
            };
          });

          // Prepend live jobs to fallback jobs so slider is always rich
          const combined = [...mapped, ...FALLBACK_VACANCIES.filter(f => !mapped.some(m => m.id === f.id))];
          setVacancies(combined);
        }
      } catch (err) {
        console.error('Error fetching vacancies:', err);
      }
    };

    void fetchLiveJobs();
    return () => { active = false; };
  }, []);

  const updateScrollState = () => {
    const el = sliderRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 10);
  };

  useEffect(() => {
    const el = sliderRef.current;
    if (!el) return;
    const observer = new ResizeObserver(updateScrollState);
    observer.observe(el);
    updateScrollState();
    return () => observer.disconnect();
  }, [vacancies]);

  const scroll = (direction: number) => {
    const el = sliderRef.current;
    if (!el) return;
    const cardWidth = 420;
    el.scrollBy({ left: direction * (cardWidth + 20), behavior: 'smooth' });
  };

  return (
    <section className="ws-vacancies-section ws-wrap" aria-labelledby="ws-vacancies-heading">
      {/* Header Toolbar */}
      <div className="ws-vacancies-toolbar">
        <div>
          <span className="ws-section-kicker flex items-center gap-2">
            <Briefcase className="w-3.5 h-3.5 text-indigo-500" />
            CARRIÈRES & VACATURES OP WERSEE
          </span>
          <h2 id="ws-vacancies-heading" className="ws-vacancies-title">
            Vind je volgende <span>rol<span className="ws-title-dot">.</span></span>
          </h2>
          <p className="ws-vacancies-subtitle">
            Brede functies, transparant salaris en flexibele uren bij innovatieve bedrijven.
          </p>
        </div>

        <div className="ws-vacancies-actions">
          <Link to="/jobs" className="ws-vacancies-view-all">
            <span>Alle vacatures</span>
            <ArrowRight className="w-4 h-4" />
          </Link>
          <div className="ws-slider-controls">
            <button 
              type="button" 
              aria-label="Vorige vacatures" 
              disabled={!canScrollLeft} 
              onClick={() => scroll(-1)}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button 
              type="button" 
              aria-label="Volgende vacatures" 
              disabled={!canScrollRight} 
              onClick={() => scroll(1)}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Slider Container */}
      <div 
        ref={sliderRef}
        onScroll={updateScrollState}
        className="ws-vacancies-slider"
        tabIndex={0}
        role="region"
        aria-label="Vacatures carrousel"
      >
        {vacancies.map((vacancy) => (
          <Link
            key={vacancy.id}
            to={`/jobs/${vacancy.id}`}
            className="ws-vacancy-card group"
            aria-label={`${vacancy.title} bij ${vacancy.company}`}
          >
            {/* Top row: Bedrijf & Locatie */}
            <div className="ws-vacancy-top">
              <div className="ws-vacancy-company-wrap">
                <div className="ws-vacancy-logo">
                  {vacancy.logoUrl ? (
                    <img 
                      src={vacancy.logoUrl} 
                      alt={vacancy.company} 
                      className="w-full h-full object-cover" 
                      loading="lazy"
                    />
                  ) : (
                    <Building2 className="w-4 h-4 text-indigo-400" />
                  )}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="ws-vacancy-company-name truncate">{vacancy.company}</span>
                    {vacancy.verified && (
                      <CheckCircle2 className="w-3.5 h-3.5 text-blue-500 shrink-0" aria-label="Geverifieerd bedrijf" />
                    )}
                  </div>
                  <span className="ws-vacancy-location flex items-center gap-1 text-[11px] text-gray-500">
                    <MapPin className="w-3 h-3 text-gray-400 shrink-0" />
                    <span className="truncate">{vacancy.location}</span>
                  </span>
                </div>
              </div>

              {/* Status / featured badge */}
              {vacancy.featured ? (
                <span className="ws-vacancy-featured-pill">
                  <Sparkles className="w-3 h-3 text-amber-500" />
                  <span>Featured</span>
                </span>
              ) : (
                <span className="ws-vacancy-open-icon group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform">
                  <ArrowUpRight className="w-3.5 h-3.5" />
                </span>
              )}
            </div>

            {/* Middle: Functie (Job Title) - broad & prominent */}
            <div className="ws-vacancy-body">
              <h3 className="ws-vacancy-role group-hover:text-indigo-400 transition-colors">
                {vacancy.title}
              </h3>
            </div>

            {/* Bottom: Salaris & Uren & Solliciteer knop */}
            <div className="ws-vacancy-bottom">
              <div className="ws-vacancy-specs">
                {/* Salaris */}
                <div className="ws-vacancy-spec ws-vacancy-salary">
                  <DollarSign className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                  <span className="font-bold text-emerald-400">{vacancy.salary}</span>
                </div>

                {/* Uren */}
                <div className="ws-vacancy-spec ws-vacancy-hours">
                  <Clock className="w-3.5 h-3.5 text-indigo-400 shrink-0" />
                  <span>{vacancy.hours}</span>
                </div>
              </div>

              <span className="ws-vacancy-apply-link">
                Bekijk
              </span>
            </div>
          </Link>
        ))}
      </div>

      {/* Slider Footer indicator */}
      <div className="ws-vacancies-footer">
        <p className="text-xs text-gray-400 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
          <span>Alle vacatures worden automatisch geverifieerd en door AI geanalyseerd op kwaliteit & spelling.</span>
        </p>
        <Link to="/workspace" className="text-xs font-semibold text-indigo-400 hover:text-indigo-300 transition-colors flex items-center gap-1">
          Vacature plaatsen als werkgever <ArrowUpRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </section>
  );
};
